import type { Size as ModalSize } from '@ps/web-core-store';
import classNames from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useSelector } from 'react-redux';

import { Size } from '@swf/core/enums';
import useBodyScrollLock from '@swf/core/hooks/useBodyScrollLock';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import { select } from '@swf/core/store';
import { getScrollingElem } from '@swf/core/utils';

import styles from './BaseModal.scss';

interface Props {
  className?: string;
  overlayClassName?: string;
  bodyOpenClassName?: string;
  isOpen: boolean;
  size: ModalSize;
  hidden?: boolean;
  showAnimation?: boolean;
  fullScreen?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  onRequestClose?(): void;
}

const scrollingElement = getScrollingElem();

const BaseModal: React.FC<Props> = ({
  showAnimation = true,
  isOpen,
  className,
  overlayClassName,
  size,
  hidden,
  onRequestClose,
  children,
  fullScreen,
  closeOnOverlayClick = false,
  closeOnEsc = false
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [overlay, setOverlay] = useState<HTMLDivElement | null>(null);
  const prevScrollTop = useRef(0);
  const { greaterThan } = useScreenSize();
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isMobile = useSelector(select.device.isMobile);

  const overlayRef = (ref: HTMLDivElement) => setOverlay(ref);

  const handleClose = useCallback(() => {
    if (!greaterThan(Size.XXSmall) && scrollTop && scrollingElement) {
      scrollingElement.scrollTop = scrollTop;
      setScrollTop(0);
    }
  }, [greaterThan, scrollTop]);

  useBodyScrollLock(isOpen, overlay);

  if (isOpen && !prevScrollTop.current && scrollingElement) {
    const scrollingElementScrollTop = scrollingElement.scrollTop;
    if (scrollingElementScrollTop) {
      setScrollTop(scrollingElementScrollTop);
      prevScrollTop.current = scrollingElementScrollTop;
    }
  }

  return useMemo(
    () => (
      <ReactModal
        bodyOpenClassName={styles.bodyOpen}
        className={classNames(
          styles.modal,
          styles[size as keyof typeof styles],
          {
            [styles.hidden]: hidden,
            [styles.modalTransition]: showAnimation
          },
          className
        )}
        isOpen={isOpen}
        onAfterClose={handleClose}
        onRequestClose={onRequestClose}
        overlayClassName={classNames(
          isMobile && fullScreen && styles.transparentBackground,
          styles.overlay,
          { [styles.inactive]: hidden, [styles.desktopEmbedded]: isDesktopEmbedded },
          styles.overlayTransition,
          overlayClassName
        )}
        shouldCloseOnOverlayClick={closeOnOverlayClick}
        shouldCloseOnEsc={closeOnEsc}
        overlayRef={overlayRef}
      >
        {children}
      </ReactModal>
    ),
    [
      size,
      hidden,
      showAnimation,
      className,
      isOpen,
      handleClose,
      onRequestClose,
      isMobile,
      fullScreen,
      isDesktopEmbedded,
      overlayClassName,
      closeOnOverlayClick,
      closeOnEsc,
      children
    ]
  );
};

export default BaseModal;
